import React, { useState } from 'react';
import {
  Badge, Box, Button, Heading, HStack, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalHeader, ModalOverlay, Spacer, Text, useColorModeValue, useToast,
} from '@chakra-ui/react';
import { FiAlertTriangle } from 'react-icons/fi';
import { useQueryClient } from 'react-query';
import GlassCard from './GlassCard';
import LedgerTable, { LedgerColumn } from './LedgerTable';
import LedgerSubline from './LedgerSubline';
import RnwyEnrollPanel from './forms/RnwyEnrollPanel';
import { usePendingRecurringEnrollments, useDeclineRecurringEnrollment } from '../hooks/useRecurringRevenues';
import { RecurringPendingEnrollment } from '../models/recurringRevenue';
import useCurrentUser from '../hooks/useCurrentUser';
import { useConfirm } from './ConfirmProvider';

// item 706 (Chad) — the Recurring section is the HOME for enrollment. A sold RNWY subscription
// (2yr/5yr) whose project has no live recurring record yet shows here, flagged NOT ENROLLED, with
// an ENROLL action that opens the same guided panel used at the invoice — so subscriptions no
// longer hide under a finalized invoice. Admin-only (enrollment fires a live Stripe subscription).
const RecurringPendingEnrollmentSection: React.FC = () => {
  const { data } = usePendingRecurringEnrollments();
  const { isAdmin } = useCurrentUser();
  const queryClient = useQueryClient();
  const decline = useDeclineRecurringEnrollment();
  const confirmDialog = useConfirm();
  const toast = useToast();
  const [enrolling, setEnrolling] = useState<RecurringPendingEnrollment | undefined>(undefined);

  // item 731 (Chad) — decline a mistaken pending enrollment: it drops off this list + the pending
  // count without touching the proposal or its invoices. Reversible from the proposal later.
  const onDecline = async (r: RecurringPendingEnrollment) => {
    const ok = await confirmDialog(
      `Remove "${r.proposalTitle}" (${r.proposalNumber}) from the subscriptions-to-enroll list? `
      + 'It stops billing nothing — the proposal and its invoices are untouched — and you can '
      + 'restore it later. Use this when the deal was flagged recurring by mistake.',
      { title: 'Decline this enrollment?', confirmLabel: 'Decline enrollment', cancelLabel: 'Keep it' },
    );
    if (!ok) return;
    try {
      await decline.mutateAsync({ proposalId: r.proposalId, declined: true });
      toast({ status: 'success', title: 'Enrollment declined', description: 'Removed from the list.' });
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not decline', description: String(e?.response?.data ?? 'Try again.') });
    }
  };

  const border = useColorModeValue('orange.200', 'whiteAlpha.300');
  const sub = useColorModeValue('gray.600', 'whiteAlpha.700');

  const rows = data?.index ?? [];
  const pending = rows.filter((r) => !r.declined);
  const declined = rows.filter((r) => r.declined);
  if (!isAdmin || rows.length === 0) return null;
  // pending first, declined after — one aligned ledger, STATUS column tells them apart
  const ordered = [...pending, ...declined];

  const closeAndRefresh = () => {
    setEnrolling(undefined);
    queryClient.invalidateQueries('recurringRevenue');
  };

  // item 731 (Chad) — a declined enrollment is reversible: restore it back to the list
  const onRestore = async (r: RecurringPendingEnrollment) => {
    try {
      await decline.mutateAsync({ proposalId: r.proposalId, declined: false });
      toast({ status: 'success', title: 'Restored', description: 'Back in the subscriptions-to-enroll list.' });
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not restore', description: String(e?.response?.data ?? 'Try again.') });
    }
  };

  // item 732 (Chad) — the standardized ledger style (aligned columns), same as the Subscriptions grid
  const columns: LedgerColumn<RecurringPendingEnrollment>[] = [
    {
      // item 1445 (Chad: "a pass on all the ledgers … the name at the bottom under the other items like the portal") —
      // PROPOSAL = number bold, title as subtext, customer · project beneath (CUSTOMER / PROJECT
      // columns folded in); the one flexible column (300px floor).
      // item 1447 — title + customer · project ride the full-width rowSubline; number here.
      key: 'proposal',
      header: 'PROPOSAL',
      width: '150px',
      sortValue: (r) => r.proposalNumber ?? '',
      render: (r) => <Text fontWeight="semibold" whiteSpace="nowrap">{r.proposalNumber || '—'}</Text>,
    },
    {
      key: 'status',
      header: 'STATUS',
      width: '120px',
      sortValue: (r) => (r.declined ? 'DECLINED' : 'PENDING'),
      // item 756 (Chad) — a sold deal that hasn't FINAL-billed yet wears a RED stamp so it's
      // visually distinct (deposit taken, final invoice still to come) — but stays fully
      // enrollable for early enrollment. Once the Final invoice generates it calms to orange.
      render: (r) => {
        if (r.declined) return <Badge colorScheme="gray">DECLINED</Badge>;
        if (!r.finalBilled) {
          return (
            <Box>
              <Badge colorScheme="red" variant="solid">PENDING</Badge>
              <Text fontSize="xs" color="red.300" mt={0.5}>not final billed</Text>
            </Box>
          );
        }
        return <Badge colorScheme="orange">PENDING</Badge>;
      },
    },
  ];

  const rowActions = (r: RecurringPendingEnrollment) => (r.declined ? (
    <Button size="xs" variant="outline" isLoading={decline.isLoading} onClick={() => onRestore(r)}>
      Restore
    </Button>
  ) : (
    <HStack spacing={2} justify="flex-end">
      <Button size="xs" variant="ghost" colorScheme="red" isLoading={decline.isLoading} onClick={() => onDecline(r)}>
        Decline
      </Button>
      <Button size="xs" colorScheme="purple" onClick={() => setEnrolling(r)}>
        Enroll
      </Button>
    </HStack>
  ));

  return (
    <>
      <GlassCard p={{ base: 4, md: 5 }} borderColor={border} borderWidth="1px">
        <HStack mb={1.5}>
          <Box color="orange.400"><FiAlertTriangle /></Box>
          <Heading size="sm">Subscriptions to enroll</Heading>
          <Badge colorScheme="orange" variant="solid" borderRadius="sm">{pending.length}</Badge>
          <Spacer />
        </HStack>
        <Text fontSize="xs" color={sub} mb={3}>
          Sold a recurring subscription that isn&apos;t enrolled yet — no Stripe billing or monthly
          commissions until you enroll.
        </Text>
        <LedgerTable<RecurringPendingEnrollment>
          rows={ordered}
          columns={columns}
          rowSubline={(r) => <LedgerSubline name={r.proposalTitle} meta={[r.customerName, r.projectName]} />}
          rowId={(r) => r.proposalId}
          rowActions={rowActions}
          rowActionsHeader=""
          rowActionsWidth="210px"
          selectable={false}
          emptyText="Nothing waiting to enroll."
        />
      </GlassCard>

      <Modal isOpen={!!enrolling} onClose={() => setEnrolling(undefined)} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{enrolling ? `Enroll — ${enrolling.customerName}` : 'Enroll subscription'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {enrolling && (
              <RnwyEnrollPanel proposalId={enrolling.proposalId} enabled onEnrolled={closeAndRefresh} />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default RecurringPendingEnrollmentSection;
